/**
 * Czech Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const cs: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Přehled',
  'nav.funds': 'Fondy',
  'nav.investments': 'Investice',
  'nav.investees': 'Příjemci investic',
  'nav.esg': 'ESG Přehled',
  'nav.compliance': 'Soulad',
  'nav.reports': 'Zprávy',
  'nav.settings': 'Nastavení',
  'nav.users': 'Uživatelé',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Ukazatele',
  'nav.taxonomy': 'Taxonomie EU',
  'nav.filings': 'Podání',
  'nav.calendar': 'Kalendář',
  'nav.submit': 'Odeslat data',
  'nav.submissions': 'Moje podání',
  'nav.benchmarks': 'Referenční hodnoty',
  'nav.messages': 'Zprávy',
  'nav.portfolio': 'Portfolio',
  'nav.performance': 'Výkonnost fondů',
  'nav.esgSummary': 'ESG Souhrn',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Hloubková kontrola',
  'nav.impact': 'Ukazatele dopadu',
  'nav.sdg': 'Mapování SDGs',
  'nav.audit': 'Auditní stopa',
  'nav.snapshots': 'Datové snímky',
  'nav.knowledgeGraph': 'Graf znalostí',
  'nav.geographic': 'Geografický',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Asistent',
  'nav.tasks': 'Úkoly',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatorní monitor',

  // Common Actions
  'action.create': 'Vytvořit',
  'action.edit': 'Upravit',
  'action.delete': 'Smazat',
  'action.save': 'Uložit',
  'action.cancel': 'Zrušit',
  'action.submit': 'Odeslat',
  'action.approve': 'Schválit',
  'action.reject': 'Zamítnout',
  'action.download': 'Stáhnout',
  'action.export': 'Exportovat',
  'action.import': 'Importovat',
  'action.filter': 'Filtrovat',
  'action.search': 'Hledat',
  'action.refresh': 'Obnovit',
  'action.close': 'Zavřít',
  'action.back': 'Zpět',
  'action.next': 'Další',
  'action.previous': 'Předchozí',
  'action.viewAll': 'Zobrazit vše',
  'action.viewDetails': 'Zobrazit detaily',
  'action.generate': 'Generovat',
  'action.signOut': 'Odhlásit se',
  'action.addNew': 'Přidat nové',
  'action.invite': 'Pozvat',

  // Statuses
  'status.draft': 'Koncept',
  'status.submitted': 'Odesláno',
  'status.validated': 'Validováno',
  'status.approved': 'Schváleno',
  'status.rejected': 'Zamítnuto',
  'status.pending': 'Čekající',
  'status.inProgress': 'Probíhá',
  'status.overdue': 'Po termínu',
  'status.compliant': 'V souladu',
  'status.filed': 'Podáno',
  'status.active': 'Aktivní',
  'status.inactive': 'Neaktivní',
  'status.closed': 'Uzavřeno',
  'status.fundraising': 'Získávání prostředků',
  'status.investing': 'Investování',
  'status.harvesting': 'Sklizeň',
  'status.validating': 'Validace',
  'status.review': 'V přezkumu',
  'status.monitoring': 'Monitorování',
  'status.exiting': 'Výstup',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Hloubková kontrola',
  'status.disbursed': 'Vyplaceno',
  'status.restructured': 'Restrukturalizováno',
  'status.matured': 'Splatné',
  'status.written_off': 'Odepsáno',
  'status.exited': 'Vystoupil',
  'status.incomplete': 'Neúplné',
  'status.open': 'Otevřené',
  'status.in_progress': 'Probíhá',
  'status.completed': 'Dokončeno',
  'status.cancelled': 'Zrušeno',
  'status.accepted': 'Přijato',
  'status.failed': 'Selhalo',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Přehled portfolia',
  'dashboard.complianceStatus': 'Stav souladu',
  'dashboard.collectionProgress': 'Průběh sběru',
  'dashboard.activityFeed': 'Aktivita',
  'dashboard.quickActions': 'Rychlé akce',
  'dashboard.investmentPipeline': 'Investiční pipeline',
  'dashboard.totalFunds': 'Celkem fondů',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktivní investice',
  'dashboard.totalInvestees': 'Příjemci investic',
  'dashboard.recentActivity': 'Nedávná aktivita',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Celkem závazků',
  'lp.capitalCalled': 'Vyžádaný kapitál',
  'lp.distributed': 'Distribuováno',
  'lp.netPosition': 'Čistá pozice',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Výkonnost fondů',
  'lp.esgSummary': 'ESG Souhrn',
  'lp.reportLibrary': 'Knihovna zpráv',
  'lp.taxonomyAligned': 'Soulad s taxonomií',
  'lp.paiCoverage': 'PAI Pokrytí',
  'lp.portfolioPerformance': 'Výkonnost LP portfolia',

  // Fund Labels
  'fund.name': 'Název fondu',
  'fund.code': 'Kód fondu',
  'fund.vintage': 'Rok vzniku',
  'fund.size': 'Velikost fondu',
  'fund.targetSize': 'Cílová velikost',
  'fund.currency': 'Měna',
  'fund.strategy': 'Strategie',
  'fund.status': 'Stav',
  'fund.sfdrCategory': 'SFDR Kategorie',
  'fund.geographyFocus': 'Geografické zaměření',
  'fund.sectorFocus': 'Sektorové zaměření',

  // Form Labels
  'form.name': 'Název',
  'form.email': 'E-mail',
  'form.country': 'Země',
  'form.amount': 'Částka',
  'form.currency': 'Měna',
  'form.date': 'Datum',
  'form.description': 'Popis',
  'form.notes': 'Poznámky',
  'form.type': 'Typ',
  'form.period': 'Období',
  'form.year': 'Rok',
  'form.required': 'Povinné',
  'form.optional': 'Nepovinné',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Závazek',
  'table.called': 'Vyžádáno',
  'table.distributed': 'Distribuováno',
  'table.tvpi': 'TVPI',
  'table.status': 'Stav',
  'table.regulation': 'Regulace',
  'table.filingType': 'Typ podání',
  'table.reportingPeriod': 'Období',
  'table.createdAt': 'Vytvořeno',
  'table.actions': 'Akce',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taxonomie EU',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Úplnost dat',
  'compliance.nextDeadline': 'Příští termín',

  // Reports
  'report.quarterly': 'Čtvrtletní zpráva',
  'report.annual': 'Výroční zpráva',
  'report.paiStatement': 'PAI Prohlášení',
  'report.taxonomyReport': 'Zpráva o taxonomii',
  'report.generatedAt': 'Vygenerováno',
  'report.version': 'Verze',

  // General
  'general.loading': 'Načítání...',
  'general.noData': 'Žádná dostupná data',
  'general.error': 'Došlo k chybě',
  'general.retry': 'Zkusit znovu',
  'general.total': 'Celkem',
  'general.page': 'Stránka',
  'general.of': 'z',
  'general.showing': 'Zobrazeno',
  'general.results': 'výsledků',
  'general.language': 'Jazyk',
  'general.english': 'Angličtina',
  'general.french': 'Francouzština',

  // Tour
  'tour.takeTour': 'Spustit prohlídku',
  'tour.skip': 'Přeskočit',
  'tour.next': 'Další',
  'tour.prev': 'Předchozí',
  'tour.finish': 'Dokončit',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigace',
  'tour.sidebarContent': 'Použijte postranní panel k navigaci mezi správou portfolia, sběrem dat ESG, dodržováním předpisů, reporty a analytickými nástroji.',
  'tour.kpisTitle': 'Přehled portfolia',
  'tour.kpisContent': 'Tyto karty ukazují vaše portfolio na první pohled — celkové fondy, AUM, aktivní investice a počet subjektů.',
  'tour.actionsTitle': 'Rychlé akce',
  'tour.actionsContent': 'Vytvářejte fondy, přidávejte investice, zvěte subjekty a generujte reporty jedním kliknutím.',
  'tour.languageTitle': 'Jazyk',
  'tour.languageContent': 'Přepínejte mezi 24 oficiálními jazyky EU. Celé rozhraní se přeloží okamžitě.',
  'tour.aiTitle': 'AI Asistent',
  'tour.aiContent': 'Ptejte se na své portfolio kdykoli. AI asistent má plný kontext o vašich fondech a investicích.',
  'action.createFund': 'Vytvořit fond',
  'action.editFund': 'Upravit fond',
  'action.addInvestment': 'Přidat investici',
  'action.addInvestee': 'Přidat subjekt',
  'action.editInvestee': 'Upravit subjekt',
  'action.createTemplate': 'Vytvořit šablonu',
  'action.createSubmission': 'Vytvořit podání',
  'action.createFiling': 'Vytvořit podání',
  'action.generateReport': 'Generovat report',
  'action.inviteUser': 'Pozvat uživatele',
  'action.sendInvitation': 'Odeslat pozvánku',
  'action.saveChanges': 'Uložit změny',
  'form.fundName': 'Název fondu',
  'form.fundCode': 'Kód fondu',
  'form.sfdrCategory': 'SFDR Kategorie',
  'form.baseCurrency': 'Základní měna',
  'form.targetSize': 'Cílová velikost',
  'form.vintageYear': 'Rok založení',
  'form.geographyFocus': 'Geografické zaměření',
  'form.sectorFocus': 'Sektorové zaměření',
  'form.investmentCode': 'Kód investice',
  'form.instrumentType': 'Typ nástroje',
  'form.maturityDate': 'Datum splatnosti',
  'form.sector': 'Sektor',
  'form.countryCode': 'Kód země',
  'form.entityType': 'Typ subjektu',
  'form.region': 'Region',
  'form.staffCount': 'Počet zaměstnanců',
  'form.borrowers': 'Dlužníci',
  'form.contactName': 'Jméno kontaktu',
  'form.contactEmail': 'Kontaktní e-mail',
  'form.templateName': 'Název šablony',
  'form.templateType': 'Typ šablony',
  'form.indicators': 'Ukazatele',
  'form.reportingPeriod': 'Reportovací období',
  'form.reportingYear': 'Rok reportu',
  'form.regulation': 'Regulace',
  'form.filingType': 'Typ podání',
  'form.reportType': 'Typ reportu',
  'form.emailAddress': 'E-mailová adresa',
  'form.role': 'Role',
  'form.selectFund': 'Vyberte fond...',
  'form.selectInvestee': 'Vyberte subjekt...',
  'table.code': 'Kód',
  'table.investee': 'Subjekt',
  'table.instrument': 'Nástroj',
  'table.amount': 'Částka',
  'table.currency': 'Měna',
  'table.maturity': 'Splatnost',
  'table.name': 'Název',
  'table.country': 'Země',
  'table.type': 'Typ',
  'table.period': 'Období',
  'table.completion': 'Dokončení',
  'table.submitted': 'Odesláno',
  'table.version': 'Verze',
  'table.indicators': 'Ukazatele',
  'table.dueDate': 'Termín',
  'table.generated': 'Vygenerováno',
  'table.email': 'E-mail',
  'table.role': 'Role',
  'error.createFailed': 'Vytvoření se nezdařilo. Zkuste to znovu.',
  'error.updateFailed': 'Aktualizace se nezdařila. Zkuste to znovu.',
  'error.loadFailed': 'Načtení dat se nezdařilo.',
};
